import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Cookie, X, Check, Settings } from 'lucide-react';
import { initClarity } from '../utils/clarity';
import useModalA11y from '../hooks/useModalA11y';
import { isPrerender } from '../utils/isPrerender';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const dialogRef = useRef(null);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // A prerender alatt a sáv NEM jelenhet meg: a build-időben kisült HTML-be
    // beleégne a teljes modál (~8 kB minden oldalon), a böngésző kirajzolná,
    // majd hidratáláskor a doboz átméreteződne. Mérve ez adta a maradék CLS-t
    // (0,2269 a /szerviz-en lassú hálózaton: a sáv teteje 692→461→325 px).
    // Kliensen mountoláskor jelenik meg – egy elem MEGJELENÉSE nem layout shift.
    if (isPrerender()) return;
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('norbiphone_cookie_consent');
    if (!cookieConsent) {
      setIsVisible(true);
    } else {
      const savedPreferences = JSON.parse(cookieConsent);
      setPreferences(savedPreferences);
      if (savedPreferences.analytics) {
        enableAnalytics();
      }
      if (savedPreferences.marketing) {
        enableMarketing();
      }
    }
  }, []);

  // Modál-a11y: fókuszcsapda + kezdő fókusz. Escape SZÁNDÉKOSAN csak a
  // beállítások-alpanelt zárja (vissza a fő sávra) – a consent-kapu maga
  // Escape-pel nem „kerülhető meg” (nem szabad némán elfogadni/elutasítani).
  const onEscape = useCallback(
    () => setShowSettings((s) => (s ? false : s)),
    []
  );
  useModalA11y(dialogRef, { active: isVisible, onEscape });

  // Bezárás utáni fókuszcél. A banner NEM gombnyomásból nyílik (oldalbetöltés),
  // ezért a hook „vissza a triggerre” visszaadása a body-n landolna (QA-mérés).
  // Döntés után a fő tartalom (main#fo-tartalom, tabindex=-1) kapja a fókuszt.
  const bannerWasVisible = useRef(false);
  useEffect(() => {
    if (bannerWasVisible.current && !isVisible) {
      document.getElementById('fo-tartalom')?.focus({ preventScroll: true });
    }
    bannerWasVisible.current = isVisible;
  }, [isVisible]);

  // Fő sáv ↔ beállítások váltásakor a megnyomott gomb unmountol, és a fókusz
  // a body-ra esne (a Tab pedig kiszökhetne a modál mögé). A váltás után ezért
  // az új nézet ELSŐ fókuszolható elemére állunk. Az első rendert kihagyjuk –
  // a kezdő fókuszt a useModalA11y adja.
  const settingsViewSwitched = useRef(false);
  useEffect(() => {
    if (!settingsViewSwitched.current) { settingsViewSwitched.current = true; return; }
    const node = dialogRef.current;
    if (!node) return;
    const first = node.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    (first || node).focus();
  }, [showSettings]);

  const enableAnalytics = () => {
    // Initialize Google Analytics if user consented
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
    // PostHog – csak statisztikai consent UTÁN töltődik be (index.html loader).
    if (typeof window !== 'undefined' && window.__npLoadAnalytics) {
      window.__npLoadAnalytics();
    }
    // Microsoft Clarity – szintén statisztikai kategória, consent után init.
    initClarity();
  };

  const enableMarketing = () => {
    // Enable marketing cookies (Meta Pixel, Google Ads, TikTok)
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
    // Meta + TikTok SDK betöltés + init + PageView – a script-ek consent
    // ELŐTT be sem töltődnek (index.html __npLoadMarketing, idempotens).
    if (typeof window !== 'undefined' && window.__npLoadMarketing) {
      window.__npLoadMarketing();
    }
  };

  const handleAcceptAll = () => {
    const newPreferences = { necessary: true, analytics: true, marketing: true };
    setPreferences(newPreferences);
    localStorage.setItem('norbiphone_cookie_consent', JSON.stringify(newPreferences));
    enableAnalytics();
    enableMarketing();
    setIsVisible(false);
  };

  // Marketing/analytics visszavonása (elutasítás vagy kikapcsolás esetén) –
  // hogy a döntés a sütik betöltése után, azonnal érvényesüljön.
  const revokeTracking = () => {
    if (typeof window === 'undefined') return;
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      });
    }
    if (window.fbq) window.fbq('consent', 'revoke');
    if (window.ttq && window.ttq.revokeConsent) window.ttq.revokeConsent();
  };

  const handleAcceptNecessary = () => {
    const newPreferences = { necessary: true, analytics: false, marketing: false };
    setPreferences(newPreferences);
    localStorage.setItem('norbiphone_cookie_consent', JSON.stringify(newPreferences));
    revokeTracking();
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('norbiphone_cookie_consent', JSON.stringify(preferences));
    // Engedélyezés vagy visszavonás kategóriánként – a döntés azonnal érvényesül.
    if (preferences.analytics) {
      enableAnalytics();
    } else if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', { 'analytics_storage': 'denied' });
    }
    if (preferences.marketing) {
      enableMarketing();
    } else {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'denied', 'ad_user_data': 'denied', 'ad_personalization': 'denied'
        });
      }
      if (typeof window !== 'undefined' && window.fbq) window.fbq('consent', 'revoke');
      if (typeof window !== 'undefined' && window.ttq && window.ttq.revokeConsent) window.ttq.revokeConsent();
    }
    setIsVisible(false);
    setShowSettings(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* ── RÉTEGSORREND – ezt a hozzájárulás-kaput SEMMI nem fedheti el ──────
          A projekt fix elemei (növekvő sorrendben):
            50    fejléc
            60    ragadós segédsávok (szűrő, kalkulátor „Tovább”)
            9985  oldal-specifikus mobil CTA-sáv
            9990  MobileCTABar
            9995  szűrő-fiók
            9998  termék- és foglalás-modál
            9999  360°-néző, lebegő kapcsolat-widget, VIP-popup
            99990 ↓ cookie-overlay
            99991 ↓ cookie-panel  ← MINDIG a legfelső
          KORÁBBAN ez 100/101 volt, tehát a fenti MINDEGYIK eleme fölé került.
          Külső auditon így is mérték: 35,5 s után a VIP-popup (akkor 99990)
          teljesen elfedte a hozzájárulás gombjait – a DOM-ban ott voltak, de
          nem lehetett rájuk kattintani. A MobileCTABar (9990) ugyanígy takarta.
          Ha új fix elemet veszel fel, ez alá tedd. ────────────────────────── */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[99990]" />

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[99991] p-4 sm:p-6">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Cookie beállítások"
          tabIndex={-1}
          className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-black/10 overflow-hidden outline-none">
          {!showSettings ? (
            // Main Banner
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-glow-from/20 to-glow-to/10 flex items-center justify-center flex-shrink-0">
                  <Cookie size={24} className="text-glow-to" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[rgb(0,55,32)] mb-2">
                    Cookie beállítások
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    Weboldalunk technikai, statisztikai és marketing sütiket használ. 
                    A marketing sütik (Meta Pixel, Google Ads) csak az Ön hozzájárulásával aktiválódnak. 
                    A hozzájárulás bármikor visszavonható.
                  </p>
                  <p className="text-gray-500 text-xs mb-4">
                    További információ: <a href="/adatkezeles/" className="text-[rgb(13,121,22)] hover:underline">Adatkezelési tájékoztató</a> | <a href="/cookie/" className="text-[rgb(13,121,22)] hover:underline">Cookie tájékoztató</a>
                  </p>
                  
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-br from-glow-from to-glow-to text-white font-semibold hover:scale-105 transition-transform shadow-md"
                    >
                      <Check size={18} />
                      Összes elfogadása
                    </button>
                    <button
                      onClick={handleAcceptNecessary}
                      className="px-6 py-2.5 rounded-full bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
                    >
                      Csak szükségesek
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-[rgb(0,55,32)] transition-colors"
                    >
                      <Settings size={18} />
                      Beállítások
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Settings Panel
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[rgb(0,55,32)]">
                  Cookie beállítások
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  aria-label="Beállítások bezárása"
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-[rgb(0,55,32)]">Szükséges sütik</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        A weboldal alapvető működéséhez szükségesek. Nem kapcsolhatók ki.
                      </p>
                    </div>
                    <div className="w-12 h-6 rounded-full bg-glow-to flex items-center justify-end px-1">
                      <div className="w-4 h-4 rounded-full bg-white shadow" />
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 rounded-xl bg-white border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-[rgb(0,55,32)]">Statisztikai sütik</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Google Analytics - látogatottsági statisztikák. Megőrzési idő: max. 14 hónap.
                      </p>
                    </div>
                    <button
                      onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                      role="switch"
                      aria-checked={preferences.analytics}
                      aria-label="Statisztikai sütik"
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.analytics ? 'bg-glow-to justify-end' : 'bg-gray-300 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 rounded-full bg-white shadow" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="p-4 rounded-xl bg-white border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-[rgb(0,55,32)]">Marketing sütik</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Meta Pixel (Facebook/Instagram) és Google Ads - hirdetések, remarketing.
                      </p>
                    </div>
                    <button
                      onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                      role="switch"
                      aria-checked={preferences.marketing}
                      aria-label="Marketing sütik"
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.marketing ? 'bg-glow-to justify-end' : 'bg-gray-300 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 rounded-full bg-white shadow" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleSavePreferences}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-br from-glow-from to-glow-to text-white font-semibold hover:scale-105 transition-transform shadow-md"
                >
                  <Check size={18} />
                  Mentés
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2.5 rounded-full bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
                >
                  Összes elfogadása
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
